import React, { Dispatch, SetStateAction } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';

import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import Checkbox from '@lib/inputs/Checkbox';
import FileUploadDragDrop from '@lib/inputs/FileUploadDragDrop';

import { SIGNATURE_FILE_ACCEPT, SIGNATURE_FILE_MAX_SIZE } from '../constants/voterRegistrationConstants';
import { GridStyledWrapper, StyledDeclarationContainer } from '../voterRegistration.styled';
import { VoterRegistrationFormError } from '../VoterRegistrationFormError';
import { IVoterRegistrationFormValues } from '../voterRegistrationTypes';

interface IVotingDeclarationSectionProps {
    setFile?: Dispatch<SetStateAction<File | null>> | undefined;
}

function VotingDeclarationSection({ setFile }: IVotingDeclarationSectionProps) {
    const intl = useIntl();
    const {
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = useFormContext();

    const isNoDLSelected = watch('identification.driverLicenseOrPennDotId');

    const handleFieldChange = (name: string, value: any) => {
        setValue(name as keyof IVoterRegistrationFormValues, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const handleFilesAccepted = (files: File[]) => {
        handleFieldChange('file', files[0]);
        setFile?.(files[0]);
        clearErrors('file');
    };

    const onFileUploadError = (errorMessages: string[][]) => {
        if (errorMessages) {
            const errorType = errorMessages[0][0];
            let errorMessage = 'signatureInvalid';

            if (errorType === 'file-invalid-type') {
                errorMessage = 'signatureFileTypeInvalid';
            } else if (errorType === 'file-too-large') {
                errorMessage = 'signatureFileSizeInvalid';
            }

            setValue('file', null);
            setError('file', {
                type: 'manual',
                message: intl.formatMessage({ id: `voterRegistration.form.error.file.${errorMessage}` }),
            });
        }
    };

    return (
        <StyledDeclarationContainer>
            <FormattedMessage id="voterRegistration.form.declaration.header" tagName="h2" />
            <p>
                <FormattedMessage id="voterRegistration.form.declaration.declare" />
            </p>
            <ul className="margin-left-5">
                <FormattedMessage id="voterRegistration.form.declaration.li.1" tagName="li" />
                <FormattedMessage id="voterRegistration.form.declaration.li.2" tagName="li" />
                <FormattedMessage id="voterRegistration.form.declaration.li.3" tagName="li" />
                <FormattedMessage id="voterRegistration.form.declaration.li.4" tagName="li" />
            </ul>
            <p>
                <FormattedMessage id="voterRegistration.form.declaration.attest" />
            </p>
            <p>
                <FormattedMessage id="voterRegistration.form.declaration.signing" />
            </p>
            <ul className="margin-left-5">
                <FormattedMessage id="voterRegistration.form.declaration.signing.li.1" tagName="li" />
                <FormattedMessage id="voterRegistration.form.declaration.signing.li.2" tagName="li" />
                <FormattedMessage id="voterRegistration.form.declaration.signing.li.3" tagName="li" />
            </ul>
            <p>
                <FormattedMessage id="voterRegistration.form.declaration.warn.1" />
            </p>
            <p>
                <FormattedMessage id="voterRegistration.form.declaration.warn.2" />
            </p>
            <GridStyledWrapper id="declaration-section">
                <div className="fullWidth margin-left-5">
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Checkbox
                            label={{
                                labelClass: ['required-true'],
                                text: <FormattedMessage id="voterRegistration.form.declaration.confirm" />,
                            }}
                            inputData={{
                                id: 'declaration',
                                name: 'declaration',
                                checked: watch('declaration'),
                                value: watch('declaration'),
                            }}
                            inputActions={{
                                handleFieldChange: val => {
                                    handleFieldChange('declaration', val);
                                },
                            }}
                        />
                        <VoterRegistrationFormError
                            name="declaration"
                            hasError={!!errors?.declaration}
                            messageId={errors?.declaration?.message}
                        />
                    </div>
                </div>
            </GridStyledWrapper>
            <AlertMessage
                type={'info'}
                content={
                    <FormattedMessage
                        id="voterRegistration.form.declaration.info.alert"
                        values={{ strong: (chunks: any) => <strong>{chunks}</strong> }}
                    />
                }
                showIcon={true}
                icon={'info-circle'}
            />
            <div className="margin-top-5" id="file-section">
                <FormattedMessage id="voterRegistration.form.declaration.signature.title" tagName="h3" />
                <FileUploadDragDrop
                    onFilesAccepted={handleFilesAccepted}
                    onFileError={onFileUploadError}
                    maxSize={SIGNATURE_FILE_MAX_SIZE}
                    accept={SIGNATURE_FILE_ACCEPT}
                    descriptionLabelId="voterRegistration.form.declaration.signature.description"
                    dragLabelId="voterRegistration.form.declaration.signature.dragLabel"
                    uploadLabelId="voterRegistration.form.declaration.signature.uploadLabel"
                    acceptFileLabelId="voterRegistration.form.declaration.signature.acceptFileLabel"
                    maxFileSizeLabelId="voterRegistration.form.declaration.signature.maxFileSizeLabel"
                    isRequired={!isNoDLSelected}
                />
                <VoterRegistrationFormError name="file" hasError={!!errors?.file} messageId={errors?.file?.message} />
            </div>
        </StyledDeclarationContainer>
    );
}

export default VotingDeclarationSection;
